import { useQueries, useQuery } from "@tanstack/react-query";
import { fileApi } from "~/api";
import type { StatusLog } from "~/types/api";

export type ItemStatusLogParams = { taskId: number; itemId: number };

export const getItemStatusLogsQueryKey = (taskId: number, itemId: number) =>
  fileApi.queryOptions("get", "/api/task/{taskId}/item/{itemId}/status-logs", {
    params: { path: { taskId, itemId } },
  }).queryKey;

export const fetchItemStatusLogs = async (taskId: number, itemId: number): Promise<StatusLog[]> => {
  const response = await fileApi.client.GET("/api/task/{taskId}/item/{itemId}/status-logs", {
    params: { path: { taskId, itemId } },
  }).then(response => {
    const sortedData = response.data?.sort((a: StatusLog, b: StatusLog) => new Date(b.createdUtc).getTime() - new Date(a.createdUtc).getTime());
    return { ...response, data: sortedData };
  });

  if (response.error) {
    throw response.error;
  }

  return (response.data || []) as StatusLog[];
};

export const useItemStatusLogs = (taskId: number, itemId: number, enabled = true) => {
  const { data, isLoading, error, refetch } = useQuery<StatusLog[]>({
    queryKey: getItemStatusLogsQueryKey(taskId, itemId),
    queryFn: async () => {
      return await fetchItemStatusLogs(taskId, itemId);
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    meta: { errorTitle: `Item Status Logs (Task: ${taskId}, Item: ${itemId})` },
  });

  return {
    statusLogs: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const useItemStatusLogsBatch = (items: ItemStatusLogParams[], enabled = true) => {
  const results = useQueries({
    queries: items.map(({ taskId, itemId }) => ({
      queryKey: getItemStatusLogsQueryKey(taskId, itemId),
      queryFn: () => fetchItemStatusLogs(taskId, itemId),
      enabled,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      meta: { errorTitle: `Item Status Logs (Task: ${taskId}, Item: ${itemId})` },
    })),
  });

  const logsByItemId = Object.fromEntries(
    items.map(({ itemId }, index) => [itemId, results[index]?.data ?? []])
  );

  const isLoading = results.some((r) => r.isLoading);

  return { logsByItemId, isLoading, results };
};
